// ShortMessage.jsx
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';
import { withScope, captureException } from '@sentry/browser';
import { useTranslation } from 'react-i18next';

import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import UniversalComponent from '../component/UniversalComponent.jsx';
import getUserProfileUrl from '../resource/getUserProfileUrl.js';
import Tooltip from '../component/Tooltip.jsx';
import { ShareType } from '../hook/useShortShareLink.js';
import useShortMessageCta, {
  CtaStatus,
  CtaType,
} from '../hook/useShortMessageCta.js';
import useHorizontalSeek from '../hook/useHorizontalSeek.js';
import VideoSeekBar from '../component/VideoSeekBar.jsx';

import { NULL_FUNCTION, EMPTY_ARRAY } from '../resource/defaults.js';

import MessageThumbnail from '../container/MessageThumbnail.js';
import WithEditMessage from '../container/WithEditMessage.js';
import WithHideMessage from '../container/WithHideMessage.js';
import WithMessagePinData from '../container/WithMessagePinData.js';
import FollowButton from '../container/FollowButton.js';
import UserStatusIndicator from '../container/UserStatusIndicator.js';
import WithMessageMetaData from '../container/WithMessageMetaData.js';
import LinkSubstring from '../container/LinkSubstring.js';
import MessageCategory from '../container/MessageCategory.js';
import UserAvatarWithStatus from '../container/UserAvatarWithStatus.js';
import ShareButton from '../container/ShareButton.js';
import Loading, { LoadingType } from '../component/Loading.jsx';
import MessageMeta from '../component/MessageMeta.jsx';
import withPlayerTrackingEvent, {
  VIDEO_CATEGORY,
} from '../resource/withPlayerTrackingEvent.js';

import { DrmFeatureName } from '../resource/drmConstants.js';
import { PLAY_STATE, LOAD_STATE } from '../resource/playerUtils.js';
import { EventTypes, ButtonId } from '../resource/trackingConstants.js';
import { MessageMoreFeature } from '../resource/messageConstants.js';
import formatNumber from '../resource/formatNumber.js';
import withFetchTranslation from '../resource/withFetchTranslation.js';
import { getIsInternalLink } from '../resource/getInternalLink.js';
import { ProfileListType } from '../resource/profileConstants.js';
import numberWithCommas from '../resource/numberWithCommas.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { PlayerType } from '../resource/engagementConstants.js';
import { LoadMode } from '../resource/playerConfig.js';

import usePreviousValue from '../hook/usePreviousValue.js';
import useUnmount from '../hook/useUnmount.js';

import {
  breakpoint,
  color,
  pinnedNotificationOnDesktopHeight,
} from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import ButtonShimmerStyle from '../style/ButtonShimmerStyle.js';
import TextEllipsis from '../style/TextEllipsis.js';
import media from '../style/media.js';
import OpacityBreath from '../style/OpacityBreath.js';
import fastForwardFadeInOut from '../style/FastForwardFadeInOut.js';
import FadeInAnimation from '../style/FadeIn.js';
import { shortsPlayerZIndex } from '../style/variables.js';

import BackIconSource from '../../img/ic_topbar_back.svg';
import MoreIconSource from '../../img/icon-more-fill-shadow.svg';

import AudioIconSource from '../../img/icon-audio-fill-shadow.svg';
import MuteIconSource from '../../img/icon-mute-fill-shadow.svg';

import ChatIconSource from '../../img/icon-chat-outline-shadow.svg';
import LikeIconSource from '../../img/icon-like-fill-shadow.svg';
import LikeOutlineIconSource from '../../img/icon-like-outline-shadow.svg';
import PlayIconSource from '../../img/ic_play.svg';
import PlayBlackIconSource from '../../img/ic_play_black.svg';
import PlayPaddingIconSource from '../../img/ic_play_padding.svg';
import PinnedGreyIconSource from '../../img/ic_pin_grey.svg';
import DiamondIconSource from '../../img/ic-diamond-md.svg';
import FastForwardIcon from '../../img/ic-fastforward.svg';
import GiftIcon from '../../img/icon-gift-outline-shadow.svg';

import { quickpayType } from '../resource/shopConstants.js';

// To avoid the short black between the poster and the video,
// we deliberately delay to hide poster instead of hiding it immediately.
// Increased delay to allow for smooth fade transition
// TODO: remote config (start)
const SHORTS_POSTER_HIDDEN_AFTER_VIDEO_PLAYED_SECONDS = 0.5;
export const TRANSITION_MILLI_SECONDS = 400;
// TODO: remote config (end)
export const METADATA_SIZE = 36;
export const METADATA_DETAIL_GAP = 12;
export const CTA_HEIGHT = 40;
export const CTA_MOBILE_HEIGHT = 36;

const ControlState = {
  TO_PLAY: 'toPlay',
};

const shouldShowPinButton = false; // TODO: enable after backend deployed

const option = { loop: true };

export const ShortMessage = forwardRef(function ShortMessage(
  {
    ratedAs = null,
    ctaDelaySeconds = 0,
    livestreamCtaDelaySeconds = 0,
    unlockPrice = null,
    fetchMessageSegments = NULL_FUNCTION,
    fetchMessageDetail = NULL_FUNCTION,
    fetchUser = NULL_FUNCTION,
    fetchMessageRating = NULL_FUNCTION,
    toggleModal = NULL_FUNCTION,
    openQuickpayModal = NULL_FUNCTION,
    setPlayerData = NULL_FUNCTION,
    submitMessageRating = NULL_FUNCTION,
    sendChatStart = NULL_FUNCTION,
    assetIds = EMPTY_ARRAY,
    messageId = null,
    meId = null,
    senderId = null,
    senderUsername = null,
    displayedSenderUsername = null,
    caption = '',
    originalCaptionText = '',
    ctaUrl = null,
    isActive = false,
    loadMode = LoadMode.NONE,
    isBalanceInsufficient = false,
    isMuted = true,
    hasAgeVerificationPinnedNotification = false,
    isMessageRatedAsFetched = false,
    isLiveStreaming = false,
    hasFirstShortMessagePlayed = false,
    isPRESTOplayEngine = false,
    hasPurchased = false,
    canViewMedia = false,
    shouldShowBack = false,
    isPublished = true,
    isFairPlay = false,
    isLanguageMatched = false,
    shouldPrefetch = false,
    setFirstShortMessagePlayed = NULL_FUNCTION,
    sendUnlockMessage = NULL_FUNCTION,
    goBack = NULL_FUNCTION,
    trackPlayerEvent = NULL_FUNCTION,
    timestampOffsetMilliseconds = 0,
    isStory = false,
    setGiftPanel = NULL_FUNCTION,
    updateEngagementData = NULL_FUNCTION,
    isFree = null,
    isNewlyUnlocked = null,
    viewScope = '',
    mediaType = '',
    shareButtonTemplates = EMPTY_ARRAY,
    unlockButtonStatus = null,
  },
  reference
) {
  const { t } = useTranslation();
  const shortMessageRef = useRef(null);
  const playButtonTransitionTimeout = useRef(null);
  const canShowPlayButtonTimeout = useRef(null);
  const player = useRef(null);
  const hasStartedPlaybackRef = useRef(false);

  const [duration, setDuration] = useState(0);
  const [stepForwardAction, setStepForwardAction] = useState(null);
  const stepForwardTimeoutRef = useRef(null); // Timer used to clear step forward/backward animation
  const stepForwardAnimationKeyRef = useRef(0); // Step forward/backward animation key counter

  const { ctaStatus, ctaType } = useShortMessageCta({
    ctaUrl,
    meId,
    isStreaming: isLiveStreaming,
    isActive,
    canViewMedia,
    isPublished,
    livestreamCtaDelaySeconds,
    ctaDelaySeconds,
    unlockPrice,
  });
  const isAuthor = useMemo(() => meId && meId === senderId, [meId, senderId]);
  const [playState, setPlayState] = useState(PLAY_STATE.Pause);
  const [loadState, setLoadState] = useState(LOAD_STATE.Loading);
  const [canShowPlayButton, setCanShowPlayButton] = useState(false);
  const [shouldShowThumbnail, setShouldShowThumbnail] = useState(true);
  const [shouldShowTranslation, setShouldShowTranslation] = useState(false);
  const [shouldShowAllCaption, setShouldShowAllCaption] = useState(false);
  const [controlState, setControlState] = useState(null);
  const prevIsActive = usePreviousValue(isActive);
  const prevLoadState = usePreviousValue(loadState);
  const prevCanViewMedia = usePreviousValue(canViewMedia);
  const isSeekBarDraggingRef = useRef(false);
  const engagementDataRef = useRef({
    isFree,
    isNewlyUnlocked,
    viewScope,
    mediaType,
    messageId,
    creatorId: senderId,
    creatorUsername: senderUsername,
  });

  // Add step forward/step backward animation
  const pushStepForwardAction = useCallback((seconds, isBackward) => {
    const action = {
      key: `step-forward-${++stepForwardAnimationKeyRef.current}`, // Generate unique key for each animation
      seconds: Math.abs(seconds),
      isBackward,
    };

    setStepForwardAction(action);

    // Clear previous timeout if exists
    if (stepForwardTimeoutRef.current) {
      clearTimeout(stepForwardTimeoutRef.current);
    }

    // Store new timeout reference
    stepForwardTimeoutRef.current = setTimeout(() => {
      setStepForwardAction(null);
      stepForwardTimeoutRef.current = null;
    }, 1000);
  }, []);

  // Create play and pause functions that can be used by the component
  const play = useCallback(() => {
    player.current?.play()?.catch(error => {
      setPlayState(PLAY_STATE.Pause);
      if (error.name === 'NotAllowedError') {
        return;
      }
      withScope(scope => {
        scope.setFingerprint(['post-video-player', error.name]);
        scope.setExtra('action', 'canplay event');
        scope.setExtra('playState', playState);
        captureException(error);
      });
    });
  }, [playState]);

  const pause = useCallback(() => {
    player.current?.pause();
  }, []);

  const togglePlay = useCallback(() => {
    if (!player.current) {
      return;
    }
    switch (playState) {
      case PLAY_STATE.Pause: {
        // To play
        setControlState(ControlState.TO_PLAY);
        clearTimeout(playButtonTransitionTimeout.current);
        playButtonTransitionTimeout.current = setTimeout(() => {
          setControlState(null);
        }, TRANSITION_MILLI_SECONDS);
        return play();
      }
      case PLAY_STATE.Playing: {
        // To pause
        // User manually pause video before `canShowPlayButton` turns true.
        if (!canShowPlayButton) {
          clearTimeout(canShowPlayButtonTimeout.current);
          setCanShowPlayButton(true);
        }
        return pause();
      }
      default: {
        return;
      }
    }
  }, [play, pause, playState, canShowPlayButton, setCanShowPlayButton]);

  // Use the useHorizontalSeek hook
  const seekData = useHorizontalSeek({
    element: shortMessageRef.current,
    player: player.current,
    isActive,
    playState,
    duration,
    play,
    pause,
    onDoubleClickFastForward: pushStepForwardAction,
    onSingleClick: togglePlay,
  });

  // Extract values from seekData
  const {
    isSeeking,
    shouldShowSeekBarOverlay,
    isSpeedUp,
    formatTime,
    displayCurrentTime,
    displayDuration,
    shouldHideUI,
  } = seekData;

  useImperativeHandle(
    reference,
    () => ({
      get player() {
        return player.current;
      },
    }),
    []
  );

  useEffect(() => {
    if (messageId && ratedAs == null && !isMessageRatedAsFetched) {
      fetchMessageRating({ id: messageId });
    }
  }, [messageId, ratedAs, isMessageRatedAsFetched, fetchMessageRating]);
  useEffect(() => {
    if (messageId && !senderId) {
      fetchMessageDetail({ messageId });
    }
  }, [messageId, senderId, fetchMessageDetail]);
  useEffect(() => {
    if (senderId && !senderUsername) {
      fetchUser({ id: senderId });
    }
  }, [senderId, senderUsername, fetchUser]);

  const handleEngagementData = useCallback(
    ({ isPlaying, isPlayerVisible }) => {
      if (player.current?.getAssetUri()) {
        // since the ShakaPlayer does not re-bind the event listeners,
        // use the engagement data useRef to catch the latest engagement data
        const {
          isFree,
          isNewlyUnlocked,
          viewScope,
          mediaType,
          messageId,
          senderId,
          senderUsername,
        } = engagementDataRef.current;

        updateEngagementData({
          playerType: PlayerType.VOD,
          isFree,
          isNewlyUnlocked,
          viewScope,
          mediaType,
          messageId,
          playerSource: player.current?.getAssetUri(),
          isPlaying,
          isPlayerVisible,
          creatorId: senderId,
          creatorUsername: senderUsername,
        });
      }
    },
    [updateEngagementData]
  );

  const handlePlayerSetup = useCallback(
    playerInstance => {
      player.current = playerInstance;
      trackPlayerEvent({
        type: EventTypes.PLAYER_VISIBLE,
        timestamp: Date.now() + timestampOffsetMilliseconds,
      });
      handleEngagementData({ isPlayerVisible: false });
    },
    [trackPlayerEvent, timestampOffsetMilliseconds, handleEngagementData]
  );

  const handleCanPlayThrough = useCallback(() => {
    setLoadState(LOAD_STATE.CanPlaythrough);
    if (!hasFirstShortMessagePlayed) {
      setFirstShortMessagePlayed({ hasPlayed: true });
    }
  }, [hasFirstShortMessagePlayed, setFirstShortMessagePlayed]);
  const handlePlay = useCallback(() => {
    if (!hasStartedPlaybackRef.current) {
      hasStartedPlaybackRef.current = true;
      trackPlayerEvent({
        type: EventTypes.PLAYER_STARTED,
        timestamp: Date.now() + timestampOffsetMilliseconds,
      });
    }
    setLoadState(LOAD_STATE.CanPlaythrough);
    setPlayState(PLAY_STATE.Playing);
    handleEngagementData({ isPlaying: true, isPlayerVisible: true });
  }, [trackPlayerEvent, timestampOffsetMilliseconds, handleEngagementData]);
  const handlePause = useCallback(() => {
    if (isSeekBarDraggingRef.current) return;
    trackPlayerEvent({
      type: EventTypes.PLAYER_STOPPED,
      timestamp: Date.now() + timestampOffsetMilliseconds,
    });
    hasStartedPlaybackRef.current = false;
    setPlayState(PLAY_STATE.Pause);
    handleEngagementData({ isPlaying: false });
  }, [trackPlayerEvent, timestampOffsetMilliseconds, handleEngagementData]);
  const handleWaiting = useCallback(() => {
    setPlayState(PLAY_STATE.Waiting);
  }, []);
  const handleSeekBarDragChange = useCallback((isDragging, wasPlaying) => {
    isSeekBarDraggingRef.current = isDragging;
    if (isDragging) {
      setPlayState(PLAY_STATE.Waiting);
    } else if (!wasPlaying) {
      setPlayState(PLAY_STATE.Pause);
    }
  }, []);
  const handleEnded = useCallback(() => {
    hasStartedPlaybackRef.current = false;
    setPlayState(PLAY_STATE.Ended);
  }, []);
  const handleProgressUpdate = useCallback(({ currentTime, duration }) => {
    if (currentTime >= SHORTS_POSTER_HIDDEN_AFTER_VIDEO_PLAYED_SECONDS) {
      setShouldShowThumbnail(false);
    }

    // Update duration for the hook
    setDuration(duration);
  }, []);

  // Play or pause message when isActive changed
  useEffect(() => {
    const isActiveChanged = prevIsActive !== isActive;
    if (isActive || isActiveChanged) {
      clearTimeout(canShowPlayButtonTimeout.current);
      canShowPlayButtonTimeout.current = setTimeout(() => {
        setCanShowPlayButton(isActive);
      }, 2000); // TODO: remote config
    }

    // (2024.06)
    // [1]
    // It used to be handled in `withPlayerTrackingEvent`
    // (within `componentWillUnmount` lifecycle),
    // but since the player instance become singleton,
    // only the active ShortMessage would get the player instance.
    // Due to that, when entering the componentWillUnmount in withPlayerTrackingEvent,
    // some trackings won't be triggered because it doesn't own player instance.
    // That's why we have to take care of this here.
    // [2]
    // `prevIsActive` might be undefined in the very beginning,
    // so just make sure that `prevIsActive` is true instead of undefined.
    if (prevIsActive && !isActive) {
      trackPlayerEvent({
        type: EventTypes.PLAYER_STOPPED,
        timestamp: Date.now() + timestampOffsetMilliseconds,
      });
      trackPlayerEvent({
        type: EventTypes.PLAYER_HIDDEN,
        timestamp: Date.now() + timestampOffsetMilliseconds,
      });
      handleEngagementData({ isPlaying: false, isPlayerVisible: false });
    }
  }, [
    isActive,
    prevIsActive,
    trackPlayerEvent,
    timestampOffsetMilliseconds,
    handleEngagementData,
  ]);
  // Play or pause message when states changed
  useEffect(() => {
    if (!isActive) {
      pause();
      setPlayState(PLAY_STATE.Pause);
      setShouldShowThumbnail(true);
    } else {
      const isActiveChanged = prevIsActive !== isActive;
      const isLoadStateChanged = prevLoadState !== loadState;
      const shouldReplayFromBeginning = prevIsActive === false && isActive;

      // Show thumbnail when becoming active for smooth transition
      if (isActiveChanged && isActive) {
        setShouldShowThumbnail(true);
      }
      if (shouldReplayFromBeginning) {
        player.current?.currentTime?.(0);
      }

      if (
        isActive &&
        [LOAD_STATE.CanPlay, LOAD_STATE.CanPlaythrough].includes(loadState) &&
        playState !== PLAY_STATE.Playing &&
        (isActiveChanged || isLoadStateChanged)
      ) {
        play();
      }
    }
  }, [
    pause,
    play,
    isActive,
    prevIsActive,
    loadState,
    prevLoadState,
    playState,
  ]);

  useEffect(() => {
    return () => {
      // Clean up all timeouts on unmount
      clearTimeout(playButtonTransitionTimeout.current);
      clearTimeout(canShowPlayButtonTimeout.current);
      if (stepForwardTimeoutRef.current) {
        clearTimeout(stepForwardTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (shouldPrefetch) {
      fetchMessageSegments({
        messageId,
        manifestType: isFairPlay ? 'm3u8' : 'mpd',
      });
    }
  }, [fetchMessageSegments, shouldPrefetch, messageId, isFairPlay]);

  useEffect(() => {
    engagementDataRef.current = {
      isFree,
      isNewlyUnlocked,
      viewScope,
      mediaType,
      messageId,
      senderId,
      senderUsername,
    };
  }, [
    isFree,
    isNewlyUnlocked,
    viewScope,
    mediaType,
    messageId,
    senderId,
    senderUsername,
  ]);

  useEffect(() => {
    if (
      playState === PLAY_STATE.Playing &&
      prevCanViewMedia != null &&
      prevCanViewMedia !== canViewMedia
    ) {
      trackPlayerEvent({
        type: EventTypes.PLAYER_STOPPED,
        timestamp: Date.now() + timestampOffsetMilliseconds,
      });
      handleEngagementData({ isPlaying: false });
    }
  }, [
    playState,
    canViewMedia,
    prevCanViewMedia,
    trackPlayerEvent,
    handleEngagementData,
    timestampOffsetMilliseconds,
  ]);

  useUnmount(() => {
    if (playState === PLAY_STATE.Playing) {
      trackPlayerEvent({
        type: EventTypes.PLAYER_STOPPED,
        timestamp: Date.now() + timestampOffsetMilliseconds,
      });
      trackPlayerEvent({
        type: EventTypes.PLAYER_HIDDEN,
        timestamp: Date.now() + timestampOffsetMilliseconds,
      });
      handleEngagementData({ isPlaying: false, isPlayerVisible: false });
    }
  });

  const displayCaption = shouldShowTranslation ? caption : originalCaptionText;
  const isLiked = ratedAs === 100;
  const isUnliked = ratedAs === 0;
  const shouldHidePlayer =
    // For Android widevine DRM session issue
    // (!isActive &&
    //   isOnAndroid &&
    //   (canViewMedia || canViewMedia === null) && // Hide player til viewScope fetched
    //   shouldUseWidevine) ||
    // Delay load second and third shorts for performance.
    // PRESTOplay uses pool-based preload, so it can mount before first play.
    (!isActive && !isPRESTOplayEngine && !hasFirstShortMessagePlayed) ||
    // Fix asset ids length changed but MessageVideoPlayer still use non-assetId manifest url
    assetIds.length === 0;
  const isControlTransition = !!controlState;
  const shouldShowPlaybackLoading =
    LOAD_STATE.CanPlaythrough !== loadState ||
    PLAY_STATE.Waiting === playState ||
    (isSeeking && PLAY_STATE.Playing !== playState);
  const shouldShowPlayerControlButton =
    ((PLAY_STATE.Pause === playState &&
      LOAD_STATE.CanPlaythrough === loadState &&
      canShowPlayButton) ||
      isControlTransition) &&
    !isSeeking &&
    !shouldShowSeekBarOverlay &&
    !shouldShowPlaybackLoading;
  const handleClickUnlock = useCallback(() => {
    if (!meId || (!hasPurchased && isBalanceInsufficient)) {
      openQuickpayModal({
        quickpayType: quickpayType.SHORTS,
      });
      return;
    }
    return sendUnlockMessage({
      id: messageId,
      shouldCheckDrm: true,
      drmFeatureName: DrmFeatureName.SHORT,
    });
  }, [
    hasPurchased,
    isBalanceInsufficient,
    meId,
    messageId,
    sendUnlockMessage,
    openQuickpayModal,
  ]);
  const CtaLinkComponent = useMemo(() => {
    if (CtaType.PUBLISH === ctaType) {
      return (
        <PublishCta
          data-element_id={ButtonId.Upload.ButtonPublish}
          data-tracking_payload={{
            messageId,
          }}
          onClick={() => {
            return toggleModal({
              modalId: 'PublishPostConfirmModal',
              payload: {
                messageId,
                listType: ProfileListType.SHORT,
              },
              isOpen: true,
            });
          }}
        >
          {t('post', {
            ns: TranslationNamespace.POST,
          })}
        </PublishCta>
      );
    }
    if (CtaType.UNLOCK === ctaType) {
      const shouldShowShimmer = !hasPurchased && isBalanceInsufficient;
      return (
        <UnlockCta
          data-element_id={
            meId
              ? ButtonId.All.ButtonUnlock
              : ButtonId.Detail.ButtonDetailSignup
          }
          data-tracking_payload={{
            messageId,
            ctaUrl,
          }}
          shouldShowShimmer={shouldShowShimmer}
          onClick={handleClickUnlock}
        >
          {meId && (hasPurchased || !isBalanceInsufficient) ? (
            'loading' === unlockButtonStatus ? (
              <Loading type={LoadingType.TEALBLUE} />
            ) : (
              <>
                {t('message_detail_unlock', {
                  ns: TranslationNamespace.POST,
                })}
                <img src={DiamondIconSource} alt="diamond" />
                <Price>
                  {unlockPrice
                    ? numberWithCommas({ number: unlockPrice })
                    : '-'}
                </Price>
              </>
            )
          ) : (
            t('cta_unlock_shorts_zero_purchase', {
              ns: TranslationNamespace.POST,
            })
          )}
        </UnlockCta>
      );
    }
    if (!ctaUrl) return null;
    const url = new URL(ctaUrl, 'http://localhost');
    const isInternal = getIsInternalLink({
      link: ctaUrl,
    });
    const linkProps = !isInternal
      ? {
          as: 'a',
          href: ctaUrl,
          target: '_blank',
          rel: 'noopener noreferrer',
        }
      : {
          to: `${url.pathname}${url.search}`,
        };
    if (CtaType.LIVESTREAM === ctaType && isLiveStreaming) {
      return (
        <LivestreamCtaLink
          {...linkProps}
          data-element_id={
            meId
              ? ButtonId.Shorts.ButtonShortsToLivestream
              : ButtonId.Shorts.ButtonVisitorShortsToLivestream
          }
          data-tracking_payload={{
            messageId,
            ctaUrl,
          }}
        >
          {meId && <img src={PlayIconSource} width="20" height="20" alt="" />}
          {t('button_watch_livestream', {
            ns: TranslationNamespace.POST,
          })}
        </LivestreamCtaLink>
      );
    }
    return (
      <CtaLink
        {...linkProps}
        data-element_id={ButtonId.Detail.ButtonWatchFullVideo}
        data-tracking_payload={{
          messageId,
          ctaUrl,
        }}
      >
        <img src={PlayBlackIconSource} width="20" height="20" alt="" />
        {t('button_watch_full_video', {
          ns: TranslationNamespace.POST,
        })}
      </CtaLink>
    );
  }, [
    ctaType,
    ctaUrl,
    isLiveStreaming,
    meId,
    messageId,
    handleClickUnlock,
    t,
    toggleModal,
    unlockButtonStatus,
    unlockPrice,
    hasPurchased,
    isBalanceInsufficient,
  ]);

  return (
    <StyledShortMessage
      shouldShowAllCaption={shouldShowAllCaption}
      ref={ref => {
        if (ref) {
          shortMessageRef.current = ref;
        }
      }}
      data-element_id={
        playState === PLAY_STATE.pause
          ? ButtonId.Detail.PlayerControlsPlay
          : playState === PLAY_STATE.Playing
            ? ButtonId.Detail.PlayerControlsPause
            : null
      }
      data-tracking_payload={{
        messageId,
      }}
    >
      {/* Speed up indicator */}
      {isSpeedUp && (
        <SpeedUpIndicator>
          <SpeedUpText>
            {t('playback_speed_2x', {
              ns: TranslationNamespace.POST,
            })}
            <SpeedUpIcon
              src={FastForwardIcon}
              width="16"
              height="16"
              alt="fast forward"
            />
          </SpeedUpText>
        </SpeedUpIndicator>
      )}

      {/* Step forward/backward animations */}
      {stepForwardAction && (
        <StepAnimation
          key={stepForwardAction.key}
          isBackward={stepForwardAction.isBackward}
        >
          <img
            src={FastForwardIcon}
            width="32"
            height="32"
            alt={
              stepForwardAction.isBackward ? 'step backward' : 'step forward'
            }
          />
          <div>
            {stepForwardAction.seconds}{' '}
            {t('second_unit', {
              ns: TranslationNamespace.POST,
            })}
          </div>
        </StepAnimation>
      )}
      {/* Video Seek Bar */}
      {isActive && (
        <VideoSeekBar
          player={player.current}
          isActive={isActive}
          displayCurrentTime={displayCurrentTime}
          displayDuration={displayDuration}
          formatTime={formatTime}
          shouldShowSeekBarOverlay={shouldShowSeekBarOverlay}
          isSeeking={isSeeking}
          playVideo={play}
          pauseVideo={pause}
          onDragStateChange={handleSeekBarDragChange}
          hasAgeVerificationPinnedNotification={
            hasAgeVerificationPinnedNotification
          }
        />
      )}

      {!shouldHidePlayer && (
        <PlayerWrapper>
          <UniversalComponent
            filename="Container_MessageVideoPlayer"
            drmFeatureName={DrmFeatureName.SHORT}
            option={option}
            messageId={messageId}
            assetId={assetIds[0]}
            alt={displayCaption}
            isMuted={isMuted}
            isActiveVideo={isActive}
            loadMode={loadMode}
            onWaiting={handleWaiting}
            onPlaying={handlePlay}
            onPause={handlePause}
            onSetup={handlePlayerSetup}
            onEnded={handleEnded}
            onCanplaythrough={handleCanPlayThrough}
            onProgressUpdate={handleProgressUpdate}
            onCanPlay={() => setLoadState(LOAD_STATE.CanPlay)}
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/MessageVideoPlayer.js')
            }
          />
        </PlayerWrapper>
      )}
      {shouldShowPlayerControlButton && !shouldHideUI && (
        <PlayerControlButton
          shouldUseAnimation={isControlTransition}
          isFadeOutTransition={controlState === ControlState.TO_PLAY}
          data-element_id={ButtonId.Detail.PlayerControlsPlay}
          data-tracking_payload={{
            messageId,
          }}
          onClick={() => {
            if (isControlTransition) {
              return;
            }
            return togglePlay();
          }}
        >
          <img src={PlayPaddingIconSource} width="60" height="60" alt="" />
        </PlayerControlButton>
      )}
      {shouldShowPlaybackLoading && (
        <LoadingWrapper>
          <Loading size={64} type={LoadingType.TEALBLUE} />
        </LoadingWrapper>
      )}

      {messageId && (
        <ThumbnailWrapper shouldShow={shouldShowThumbnail}>
          <MessageThumbnail id={messageId} />
        </ThumbnailWrapper>
      )}

      {shouldShowBack && (
        <BackButton
          data-element_id={ButtonId.All.ButtonBack}
          data-tracking_payload={{
            messageId,
          }}
          onClick={() => {
            return goBack();
          }}
        >
          <img src={BackIconSource} width="28" height="28" alt="go back" />
        </BackButton>
      )}

      {isPublished && (
        <MoreButton
          data-element_id={ButtonId.All.ButtonMore}
          data-tracking_payload={{
            messageId,
          }}
          onClick={() => {
            return toggleModal({
              modalId: 'MessageMoreModal',
              payload: {
                messageId,
                pinProfileListType: ProfileListType.SHORT,
                features: [
                  ...(meId && meId !== senderId
                    ? [MessageMoreFeature.BLOCK]
                    : []),
                  MessageMoreFeature.FOLLOW,
                  MessageMoreFeature.PROFILE,
                  ...(shouldShowPinButton ? [MessageMoreFeature.PIN] : []),
                  MessageMoreFeature.EDIT,
                  MessageMoreFeature.HIDE,
                ],
              },
              isOpen: true,
            });
          }}
        >
          <img src={MoreIconSource} width="28" height="28" alt="more" />
        </MoreButton>
      )}
      <ActionWrapper
        $hasAgeVerificationPinnedNotification={
          hasAgeVerificationPinnedNotification
        }
      >
        <MessageDetail
          ctaTransitionStatus={ctaStatus}
          shouldHideUI={shouldHideUI}
        >
          <WithMessagePinData
            messageId={messageId}
            profileListType={ProfileListType.SHORT}
          >
            {({ isPinned }) =>
              isPinned ? (
                <Pinned>
                  <PinIcon src={PinnedGreyIconSource} width="18" height="18" />
                  <PinText>
                    {t('pinned_post', {
                      ns: TranslationNamespace.POST,
                    })}
                  </PinText>
                </Pinned>
              ) : null
            }
          </WithMessagePinData>
          {!senderId || !displayedSenderUsername ? (
            <User isPlaceholder />
          ) : (
            <User>
              <Username
                to={
                  senderId
                    ? getUserProfileUrl({
                        userId: senderId,
                        username: senderUsername,
                      })
                    : ''
                }
                data-element_id={ButtonId.Detail.ContentDetailName}
                data-tracking_payload={{
                  'user.id': senderId,
                  'user.username': senderUsername,
                }}
              >
                {displayedSenderUsername}
              </Username>
              <UserStatusIndicatorWrapper>
                <UserStatusIndicator
                  userId={senderId}
                  shouldShowOnline
                  shouldShowLiveStream
                />
              </UserStatusIndicatorWrapper>
              <FollowButtonWrapper>
                <FollowButton userId={senderId} />
              </FollowButtonWrapper>
            </User>
          )}
          <Caption
            isPlaceholder={!messageId}
            className="swiper-no-swiping"
            shouldShowAllCaption={shouldShowAllCaption}
            data-element_id={
              shouldShowAllCaption
                ? ButtonId.Shorts.ShortsCaptionClose
                : ButtonId.Shorts.ShortsCaptionOpen
            }
            data-tracking_payload={{
              messageId,
            }}
            onClick={e => {
              e.stopPropagation();
              return setShouldShowAllCaption(!shouldShowAllCaption);
            }}
          >
            {shouldShowAllCaption ? (
              <LinkSubstring>{displayCaption}</LinkSubstring>
            ) : (
              displayCaption
            )}
          </Caption>
          <MessageCategory
            messageId={messageId}
            styles={{ paddingBottom: '8px' }}
          />
          {displayCaption && !isLanguageMatched && (
            <TranslationButton
              data-element_id={
                shouldShowTranslation
                  ? ButtonId.All.ButtonTextOriginal
                  : ButtonId.All.ButtonTextTranslate
              }
              data-tracking_payload={{
                messageId,
              }}
              onClick={() => {
                return setShouldShowTranslation(!shouldShowTranslation);
              }}
            >
              {t(shouldShowTranslation ? 'show_original' : 'show_translate', {
                ns: TranslationNamespace.POST,
              })}
            </TranslationButton>
          )}
          <MetaRow>
            <WithMessageMetaData messageId={messageId}>
              {({ viewCount, unlocks, expiresAtUnix, isPublished }) => (
                <MessageMeta
                  id={messageId}
                  showExpires={isStory}
                  showUnlocks={CtaType.UNLOCK === ctaType}
                  showViewCount={CtaType.UNLOCK !== ctaType}
                  showLikes={false}
                  viewCount={viewCount}
                  unlockCount={unlocks}
                  expiresAtUnix={expiresAtUnix}
                  isPublished={isPublished}
                />
              )}
            </WithMessageMetaData>
          </MetaRow>
          {CtaLinkComponent}
        </MessageDetail>
        <MessageMetadata shouldHideUI={shouldHideUI}>
          <UserAvatarWrapper>
            <UserAvatarWithStatus
              id={senderId}
              size={36}
              pathname={ProfileListType.SHORT}
              circleOffset={2}
              shouldShowLiveStream
              livestreamSize={14}
            />
          </UserAvatarWrapper>
          {isPublished && (
            <WithMessageMetaData messageId={messageId}>
              {({ likes = 0 }) => {
                return (
                  <>
                    <Tooltip
                      position="left"
                      renderHint={() => (
                        <TooltipText isLikeTooltip>
                          {t('general_like', {
                            ns: TranslationNamespace.POST,
                          })}
                        </TooltipText>
                      )}
                      trigger="hover"
                    >
                      <LikeButton
                        data-element_id={ButtonId.All.ButtonLike}
                        data-tracking_payload={{
                          messageId,
                        }}
                        onClick={() => {
                          return submitMessageRating({
                            id: messageId,
                            rating: 100,
                          });
                        }}
                      >
                        <img
                          src={isLiked ? LikeIconSource : LikeOutlineIconSource}
                          width="32"
                          height="32"
                          alt=""
                        />
                        <MetadataText>{formatNumber(likes)}</MetadataText>
                      </LikeButton>
                    </Tooltip>
                  </>
                );
              }}
            </WithMessageMetaData>
          )}
          {isPublished && (
            <Tooltip
              position="left"
              renderHint={() => (
                <TooltipText>
                  {t('general_dislike', {
                    ns: TranslationNamespace.POST,
                  })}
                </TooltipText>
              )}
              trigger="hover"
            >
              <UnlikeButton
                data-element_id={ButtonId.All.ButtonDislike}
                data-tracking_payload={{
                  messageId,
                }}
                onClick={() => {
                  return submitMessageRating({
                    id: messageId,
                    rating: 0,
                  });
                }}
              >
                <img
                  src={isUnliked ? LikeIconSource : LikeOutlineIconSource}
                  width="32"
                  height="32"
                  alt="unlike"
                />
              </UnlikeButton>
            </Tooltip>
          )}
          {!isAuthor && isPublished && (
            <Tooltip
              position="left"
              renderHint={() => (
                <TooltipText>
                  {t('tabbar_chat', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </TooltipText>
              )}
              trigger="hover"
            >
              <ChatButton
                data-element_id={ButtonId.All.ButtonChat}
                data-tracking_payload={{
                  messageId,
                }}
                onClick={() => {
                  return sendChatStart({ userId: senderId });
                }}
              >
                <img src={ChatIconSource} width="32" height="32" alt="chat" />
              </ChatButton>
            </Tooltip>
          )}

          {isPublished && (
            <Tooltip
              position="left"
              renderHint={() => (
                <TooltipText>
                  {t('giftname_default', {
                    ns: TranslationNamespace.POST,
                  })}
                </TooltipText>
              )}
              trigger="hover"
            >
              <GiftButton
                onClick={async () => {
                  await sendChatStart({ userId: senderId });
                  setGiftPanel({ isOpen: true });
                }}
              >
                <img src={GiftIcon} width="36" height="36" alt="gift" />
              </GiftButton>
            </Tooltip>
          )}

          {isPublished && (
            <ShareButtonWrapper>
              <ShareButton
                id={messageId}
                shareType={ShareType.SHORT}
                isAutoHandleShareText={true}
                username={senderUsername}
                copyLinkBoxPosition={'left'}
                tooltipPosition={'left'}
                templates={shareButtonTemplates}
              />
            </ShareButtonWrapper>
          )}
          {!isPublished && (
            <WithEditMessage messageId={messageId}>
              {({ handleOnClick, iconSource }) => (
                <Tooltip
                  position="left"
                  renderHint={() => (
                    <TooltipText>
                      {t('edit', {
                        ns: TranslationNamespace.POST,
                      })}
                    </TooltipText>
                  )}
                  trigger="hover"
                >
                  <EditButton
                    data-element_id={ButtonId.All.ButtonEdit}
                    data-tracking_payload={{
                      'message.id': messageId,
                    }}
                    onClick={handleOnClick}
                  >
                    <img src={iconSource} width="32" height="32" alt="edit" />
                  </EditButton>
                </Tooltip>
              )}
            </WithEditMessage>
          )}
          {!isPublished && (
            <WithHideMessage messageId={messageId}>
              {({ handleOnClick, iconSource }) => (
                <Tooltip
                  position="left"
                  renderHint={() => (
                    <TooltipText>
                      {t('hide', {
                        ns: TranslationNamespace.POST,
                      })}
                    </TooltipText>
                  )}
                  trigger="hover"
                >
                  <HideButton
                    data-element_id={ButtonId.All.ButtonHiddenMassage}
                    data-tracking_payload={{
                      'message.id': messageId,
                    }}
                    onClick={handleOnClick}
                  >
                    <img src={iconSource} width="32" height="32" alt="hide" />
                  </HideButton>
                </Tooltip>
              )}
            </WithHideMessage>
          )}
          <Tooltip
            position="left"
            renderHint={() => (
              <TooltipText>{isMuted ? 'Off' : 'On'}</TooltipText>
            )}
            trigger="hover"
          >
            <MuteButton
              data-element_id={
                isMuted ? ButtonId.All.ButtonMuted : ButtonId.All.ButtonVolume
              }
              data-tracking_payload={{
                messageId,
                isMuted,
              }}
              onClick={() => {
                return setPlayerData({ isMuted: !isMuted });
              }}
            >
              <img
                src={isMuted ? MuteIconSource : AudioIconSource}
                width="32"
                height="32"
                alt={isMuted ? 'audio off' : 'audio on'}
              />
            </MuteButton>
          </Tooltip>
        </MessageMetadata>
      </ActionWrapper>
    </StyledShortMessage>
  );
});

ShortMessage.propTypes = {
  ratedAs: PropTypes.number,
  ctaDelaySeconds: PropTypes.number,
  livestreamCtaDelaySeconds: PropTypes.number,
  unlockPrice: PropTypes.number,
  timestampOffsetMilliseconds: PropTypes.number,
  fetchMessageSegments: PropTypes.func,
  fetchMessageDetail: PropTypes.func,
  fetchUser: PropTypes.func,
  fetchMessageRating: PropTypes.func,
  toggleModal: PropTypes.func,
  openQuickpayModal: PropTypes.func,
  setPlayerData: PropTypes.func,
  submitMessageRating: PropTypes.func,
  sendChatStart: PropTypes.func,
  meId: PropTypes.string,
  senderId: PropTypes.string,
  senderUsername: PropTypes.string,
  displayedSenderUsername: PropTypes.string,
  messageId: PropTypes.string,
  caption: PropTypes.string,
  originalCaptionText: PropTypes.string,
  ctaUrl: PropTypes.string,
  assetIds: PropTypes.array,
  isActive: PropTypes.bool,
  loadMode: PropTypes.string,
  isBalanceInsufficient: PropTypes.bool,
  isMuted: PropTypes.bool,
  hasAgeVerificationPinnedNotification: PropTypes.bool,
  isMessageRatedAsFetched: PropTypes.bool,
  isLiveStreaming: PropTypes.bool,
  hasFirstShortMessagePlayed: PropTypes.bool,
  isPRESTOplayEngine: PropTypes.bool,
  hasPurchased: PropTypes.bool,
  canViewMedia: PropTypes.bool,
  shouldShowBack: PropTypes.bool,
  isPublished: PropTypes.bool,
  isFairPlay: PropTypes.bool,
  shouldPrefetch: PropTypes.bool,
  isLanguageMatched: PropTypes.bool,
  trackPlayerEvent: PropTypes.func,
  setFirstShortMessagePlayed: PropTypes.func,
  sendUnlockMessage: PropTypes.func,
  goBack: PropTypes.func,
  isStory: PropTypes.bool,
  setGiftPanel: PropTypes.func,
  updateEngagementData: PropTypes.func,
  isFree: PropTypes.bool,
  isNewlyUnlocked: PropTypes.bool,
  viewScope: PropTypes.string,
  mediaType: PropTypes.string,
  shareButtonTemplates: PropTypes.array,
  unlockButtonStatus: PropTypes.string,
};

const StyledShortMessage = styled.div`
  position: relative;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  overflow: hidden;
  aspect-ratio: 9 / 16;
  user-select: none;
  @supports not (aspect-ratio: 9 / 16) {
    padding-top: 178%;
    height: 0;
  }
  ${media.mobile`
    width: auto;
    height: 100%;
    aspect-ratio: auto;
    padding-top: 0; 
  `};

  @media (min-width: ${breakpoint.mobile}px) {
    border-radius: 8px;
  }
`;

const ThumbnailWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: 8px;
  overflow: hidden;
  opacity: ${({ shouldShow }) => (shouldShow ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
  z-index: 1;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  pointer-events: none;

  ${media.mobile`
    border-radius: 0px;
  `}
`;

const BackButton = styled.button`
  ${ResetButtonStyle};
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: ${shortsPlayerZIndex.back};
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  > img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
  @media (hover: hover) {
    :hover {
      border-radius: 50%;
      background-color: rgba(155, 155, 155, 0.2);
    }
  }
`;

const MoreButton = styled.button`
  ${ResetButtonStyle};
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: ${shortsPlayerZIndex.more};
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  > img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
  @media (hover: hover) {
    :hover {
      border-radius: 50%;
      background-color: rgba(155, 155, 155, 0.2);
    }
  }
`;

const ActionWrapper = styled.div`
  position: absolute;
  bottom: ${({ $hasAgeVerificationPinnedNotification }) =>
    $hasAgeVerificationPinnedNotification
      ? pinnedNotificationOnDesktopHeight
      : 0}px;
  left: 0px;
  right: 0px;
  z-index: ${shortsPlayerZIndex.actionWrapper};
  // padding right + gap + metadata
  padding: 0px ${16 + METADATA_DETAIL_GAP + METADATA_SIZE}px 16px 16px;
  user-select: text;
  ${media.tablet`
    bottom: 0;
  `}
  ${media.mobile`
    padding: 0px ${16 + METADATA_DETAIL_GAP + METADATA_SIZE}px 16px 16px;
  `}
`;

const FadeOut = keyframes`
  from  {
    opacity: 1;
    transform: scale(1);
  }
  to  {
    opacity: 0;
    transform: scale(2);
  }
`;

const FadeIn = keyframes`
  from  {
    opacity: 0;
    transform: scale(2);
  }
  to  {
    opacity: 1;
    transform: scale(1);
  }
`;

const PlayerControlButton = styled.button`
  ${ResetButtonStyle};
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: ${shortsPlayerZIndex.playerControl};
  transform: translate(-50%, -50%);
  > img {
    border-radius: 50%;
    padding: 6px;
    width: 72px;
    height: 72px;
    object-fit: contain;
    background-color: rgba(25, 25, 25, 0.6);
    ${({ shouldUseAnimation, isFadeOutTransition }) =>
      shouldUseAnimation
        ? css`
            animation: ${TRANSITION_MILLI_SECONDS}ms forwards
              ${isFadeOutTransition ? FadeOut : FadeIn} ease-out;
          `
        : css``}
  }
`;

const MessageDetail = styled.div`
  overflow: hidden;
  text-align: left;
  transition:
    transform 0.4s,
    opacity 0.2s ease-in-out;
  opacity: ${({ shouldHideUI }) => (shouldHideUI ? 0 : 1)};
  pointer-events: ${({ shouldHideUI }) => (shouldHideUI ? 'none' : 'auto')};
  // add 1px to solve swaglive/product#5010
  transform: translateY(
    ${({ ctaTransitionStatus }) =>
      CtaStatus.HIDDEN === ctaTransitionStatus ? CTA_HEIGHT + 24 + 1 : 0}px
  );
  ${media.mobile`
    // add 1px to solve swaglive/product#5010
    transform: translateY(
      ${({ ctaTransitionStatus }) =>
        CtaStatus.HIDDEN === ctaTransitionStatus
          ? CTA_MOBILE_HEIGHT + 24 + 1
          : 0}px
    );
  `};
`;

const Pinned = styled.div`
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const PinIcon = styled.img`
  margin-right: 4px;
  width: 18px;
  height: 18px;
  object-fit: contain;
`;

const PinText = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  color: ${color.neutral[55]};
  ${media.mobile`
    font-size: 12px;
  `};
`;

const User = styled.div`
  margin-bottom: 8px;
  max-width: ${({ isPlaceholder }) => (isPlaceholder ? '30%' : 'auto')};
  min-height: ${({ isPlaceholder }) => (isPlaceholder ? '22px' : 'auto')};
  display: flex;
  align-items: center;
  background-color: ${({ isPlaceholder }) =>
    isPlaceholder ? color.neutral[10] : 'transparent'};
`;

const Username = styled(Link)`
  ${TextEllipsis};
  font-size: 16px;
  color: ${color.neutral[100]};
  line-height: 1.4;
`;

const UserStatusIndicatorWrapper = styled.span`
  flex: none;
  margin-left: 4px;
  &:empty {
    margin-left: 0px;
  }
`;

const FollowButtonWrapper = styled.div`
  margin-left: 10px;
  flex: none;
`;

const Caption = styled.div.attrs(({ isPlaceholder }) => ({
  style: {
    backgroundColor: isPlaceholder ? color.neutral[10] : 'transparent',
    minHeight: isPlaceholder ? '20px' : 'auto',
  },
}))`
  text-overflow: ellipsis;
  height: auto;
  margin-bottom: 8px;
  max-width: 100%;
  color: ${color.neutral[100]};
  font-size: 14px;
  line-height: 1.4;
  text-align: left;
  white-space: pre-wrap;
  cursor: pointer;
  &::-webkit-scrollbar {
    display: none;
  }
  ${({ shouldShowAllCaption }) => {
    if (shouldShowAllCaption) {
      return css`
        max-height: 40vh;
        display: block;
        overflow-y: scroll;
        overflow-x: hidden;
      `;
    }
    return css`
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
    `;
  }}
`;

const TranslationButton = styled.button`
  ${ResetButtonStyle};
  font-size: 12px;
  font-weight: 600;
  color: ${color.neutral[100]};
  :last-child {
    margin-bottom: 0px;
  }
`;

const CtaStyle = css`
  ${ResetButtonStyle};
  border-radius: 4px;
  padding: 0px 12px;
  width: 100%;
  height: ${CTA_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${color.neutral[0]};
  font-size: 20px;
  font-weight: 600;
  background-color: ${color.tealBlue};
  line-height: 150%;
  letter-spacing: 0.014px;
  margin-top: 16px;

  > img {
    margin-right: 8px;
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
  ${media.mobile`
    height: ${CTA_MOBILE_HEIGHT}px;
    font-size: 14px;
    > img {
      margin-right: 4px;
      width: 16px;
      height: 16px;
    }
  `};
`;

const CtaLink = styled(Link)`
  ${CtaStyle};
`;

const LivestreamCtaLink = styled(Link)`
  ${CtaStyle};
  background-color: 'transparent';
  background-image: linear-gradient(
    136deg,
    #a445b3 3.79%,
    #d41872 48.03%,
    #ff0066 97.19%
  );
  color: ${color.neutral[100]};
`;

const UnlockCta = styled(({ shouldShowShimmer, ...restProps }) => (
  <button {...restProps} />
))`
  ${CtaStyle};
  ${({ shouldShowShimmer }) => shouldShowShimmer && ButtonShimmerStyle};
  --button-shine-color: ${color.tealBlue_alpha_10};
  border: 1px solid ${color.tealBlue};
  color: ${color.tealBlue};
  background-color: rgba(35, 35, 35, 0.4);
  > img {
    width: 24px;
    height: 24px;
    object-fit: contain;
    margin-left: 8px;
    margin-right: 4px;
  }
  ${media.mobile`
    font-size: 14px;
    font-weight: 600;
    > img {
      width: 12px;
      height: 12px;
      margin-left: 4px;
      margin-right: 2px;
    }
  `};
`;

const PublishCta = styled.button`
  ${CtaStyle};
`;

const Price = styled.span`
  color: ${color.tealBlue};
  font-size: 22px;
  font-weight: 600;
  ${media.mobile`
    font-size: 14px;
  `};
`;

const MessageMetadata = styled.div`
  position: absolute;
  bottom: 20px;
  right: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: opacity 0.2s ease-in-out;
  opacity: ${({ shouldHideUI }) => (shouldHideUI ? 0 : 1)};
  pointer-events: ${({ shouldHideUI }) => (shouldHideUI ? 'none' : 'auto')};
  > div {
    width: ${METADATA_SIZE}px;
  }
`;

const MetadataStyle = css`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  font-size: 12px;
  font-weight: 600;
  color: ${color.neutral[100]};
  text-align: center;
  :last-child {
    margin-bottom: 0px;
  }
  > img {
    padding: 4px;
    width: ${METADATA_SIZE}px;
    height: ${METADATA_SIZE}px;
    object-fit: contain;
    @media (hover: hover) {
      :hover {
        border-radius: 50%;
        background-color: ${color.neutral['100_alpha_40']};
      }
    }
  }
`;

const UserAvatarWrapper = styled.div`
  ${MetadataStyle};
`;

const MetadataText = styled.div`
  ${TextEllipsis};
  max-width: ${METADATA_SIZE}px;
  line-height: 1.4;
`;

const LikeButton = styled.button`
  ${ResetButtonStyle};
  ${MetadataStyle};
`;

const UnlikeButton = styled.button`
  ${ResetButtonStyle};
  ${MetadataStyle};
  > img {
    transform: rotate(180deg);
  }
`;

const ChatButton = styled.button`
  ${ResetButtonStyle};
  ${MetadataStyle};
`;

const ShareButtonWrapper = styled.div`
  ${MetadataStyle};
`;

const MuteButton = styled.button`
  ${ResetButtonStyle};
  ${MetadataStyle};
  margin-bottom: 0;
`;

const HideButton = styled.button`
  ${ResetButtonStyle};
  ${MetadataStyle};
`;

const EditButton = styled.button`
  ${ResetButtonStyle};
  ${MetadataStyle};
`;

const PlayerWrapper = styled.div`
  z-index: ${shortsPlayerZIndex.playerWrapper};
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 8px;
  @supports not (aspect-ratio: 9 / 16) {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
  }

  ${media.mobile`
    border-radius: 0px;
    position: static;
  `}
`;

const LoadingWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: ${shortsPlayerZIndex.actionWrapper};
`;

const TooltipText = styled.div`
  height: 30px;
  margin-left: -28px;
  padding: 8px;
  border-radius: 8px;
  box-shadow: 0 2px 8px 0 ${color.neutral['0_alpha_80']};
  background-color: ${color.neutral['95']};
  color: ${color.black};
  text-align: center;
  white-space: nowrap;
  font-size: 14px;
  transform: translateY(-${({ isLikeTooltip }) => (isLikeTooltip ? 16 : 8)}px);
`;

const SpeedUpIndicator = styled.div`
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translate(-50%, 0);
  z-index: ${shortsPlayerZIndex.actionWrapper};
  background: ${color.neutral['20_alpha_80']};
  border-radius: 6px;
  backdrop-filter: blur(10px);

  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  pointer-events: none;
  ${OpacityBreath({ duration: '0.8' })}
`;

const SpeedUpText = styled.div`
  color: ${color.neutral[100]};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`;

const SpeedUpIcon = styled.img`
  filter: brightness(0) invert(1);
  opacity: 0.9;
  flex-shrink: 0;
`;

const StepAnimation = styled.div`
  position: absolute;
  top: 0;
  height: 100vh;
  width: 80px;
  z-index: ${shortsPlayerZIndex.seekBar};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  pointer-events: none;
  color: white;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  animation: ${FadeInAnimation} 0.5s ease-in-out;

  ${({ isBackward }) =>
    isBackward
      ? css`
          left: 0;
          background: linear-gradient(
            90deg,
            rgba(0, 0, 0, 0.5) 0%,
            rgba(0, 0, 0, 0.3) 30%,
            rgba(0, 0, 0, 0.1) 60%,
            rgba(0, 0, 0, 0) 100%
          );
        `
      : css`
          right: 0;
          background: linear-gradient(
            270deg,
            rgba(0, 0, 0, 0.5) 0%,
            rgba(0, 0, 0, 0.3) 30%,
            rgba(0, 0, 0, 0.1) 60%,
            rgba(0, 0, 0, 0) 100%
          );
        `}

  img {
    animation: ${({ isBackward }) =>
        fastForwardFadeInOut({ isRewind: isBackward })}
      1s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
    filter: brightness(0) invert(1);
    opacity: 1;
    flex-shrink: 0;
  }
`;

const GiftButton = styled.div`
  ${ResetButtonStyle};
  ${MetadataStyle};
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
`;

export default withFetchTranslation(
  withPlayerTrackingEvent(ShortMessage, VIDEO_CATEGORY.SHORTS)
);
